<template>
  <div class="community-space-manage-container">
    <el-row :gutter="16">
      <!-- 左侧场馆导航：保留原有 add / edit / delete 弹窗入口与选中联动 -->
      <el-col :xs="24" :sm="24" :md="5" :lg="4">
        <div class="ds-nav-card">
          <div class="ds-nav-card__header">
            <span>{{ $t('communitySpaceManage.venueInfo') }}</span>
          </div>
          <div class="ds-nav-card__actions">
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddVenueModal">
              {{ $t('communitySpaceManage.add') }}
            </el-button>
            <el-button size="mini" icon="el-icon-edit" @click="openEditVenueModal">
              {{ $t('communitySpaceManage.edit') }}
            </el-button>
            <el-button type="danger" size="mini" icon="el-icon-delete" @click="openDeleteVenueModal">
              {{ $t('communitySpaceManage.delete') }}
            </el-button>
          </div>
          <el-scrollbar class="ds-nav-card__scroll">
            <ul class="ds-nav-card__list">
              <li v-for="item in venues" :key="item.venueId" class="ds-nav-card__item"
                :class="{ 'is-active': conditions.venueId === item.venueId }" @click="selectVenue(item)">
                <i class="el-icon-office-building"></i>
                <span class="ds-nav-card__name">{{ item.name }}</span>
              </li>
            </ul>
          </el-scrollbar>
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="19" :lg="20">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('communitySpaceManage.page.title')" :subtitle="$t('communitySpaceManage.page.subtitle')" />

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="conditions.spaceId" clearable
                :placeholder="$t('communitySpaceManage.selectSpaceId')" @keyup.enter.native="querySpaces" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="conditions.name" clearable
                :placeholder="$t('communitySpaceManage.selectSpace')" @keyup.enter.native="querySpaces" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.state" :placeholder="$t('communitySpaceManage.selectStatus')" clearable
                class="w-full">
                <el-option :label="$t('communitySpaceManage.selectStatus')" value="" />
                <el-option :label="$t('communitySpaceManage.status1001')" value="1001" />
                <el-option :label="$t('communitySpaceManage.status2002')" value="2002" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="querySpaces">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetConditions">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="fetchSpaces">
            <template #left>
              <span class="ds-panel__title">{{ $t('communitySpaceManage.spaceInfo') }}</span>
              <el-tooltip :content="$t('communitySpaceManage.page.currentVenueTip')" placement="top">
                <i class="el-icon-info ds-panel__info" />
              </el-tooltip>
            </template>
            <template #right>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddSpaceModal">
                {{ $t('communitySpaceManage.addSpace') }}
              </el-button>
            </template>
          </ds-toolbar>

          <ds-data-table :data="spaces" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('communitySpaceManage.page.empty')" @retry="fetchSpaces">
            <el-table-column prop="spaceId" :label="$t('communitySpaceManage.spaceId')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.spaceId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" :label="$t('communitySpaceManage.name')" align="left" min-width="140"
              show-overflow-tooltip />
            <el-table-column :label="$t('communitySpaceManage.startTime')" align="center" width="130">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('communitySpaceManage.endTime')" align="center" width="130">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('communitySpaceManage.feeMoney')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.feeMoney) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="adminName" :label="$t('communitySpaceManage.adminName')" align="center" width="100" />
            <el-table-column prop="tel" :label="$t('communitySpaceManage.tel')" align="center" width="130" />
            <el-table-column :label="$t('communitySpaceManage.state')" align="center" width="120">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveSpaceState(scope.row)" :label="resolveSpaceStateLabel(scope.row)" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('communitySpaceManage.operation')" align="center" width="240" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" @click="openEditOpenTime(scope.row)">
                  {{ $t('communitySpaceManage.openTime') }}
                </el-button>
                <el-button size="mini" type="primary" icon="el-icon-edit" @click="openEditSpaceModal(scope.row)">
                  {{ $t('communitySpaceManage.edit') }}
                </el-button>
                <el-button size="mini" type="danger" icon="el-icon-delete" @click="openDeleteSpaceModal(scope.row)">
                  {{ $t('communitySpaceManage.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handlePageChange" />
        </div>
      </el-col>
    </el-row>

    <!-- 子组件 -->
    <add-community-space ref="addSpaceModal" @success="fetchSpaces" />
    <edit-community-space ref="editSpaceModal" @success="fetchSpaces" />
    <edit-community-space-open-time ref="editOpenTimeModal" />
    <delete-community-space ref="deleteSpaceModal" @success="fetchSpaces" />
    <add-community-venue ref="addVenueModal" @success="listVenues" />
    <edit-community-venue ref="editVenueModal" @success="listVenues" />
    <delete-community-venue ref="deleteVenueModal" @success="listVenues" />
  </div>
</template>

<script>
import { listCommunityVenue, listCommunitySpace } from '@/api/community/communitySpaceManageApi'
import AddCommunitySpace from '@/components/community/AddCommunitySpace'
import EditCommunitySpace from '@/components/community/EditCommunitySpace'
import EditCommunitySpaceOpenTime from '@/components/community/EditCommunitySpaceOpenTime'
import DeleteCommunitySpace from '@/components/community/DeleteCommunitySpace'
import AddCommunityVenue from '@/components/community/AddCommunityVenue'
import EditCommunityVenue from '@/components/community/EditCommunityVenue'
import DeleteCommunityVenue from '@/components/community/DeleteCommunityVenue'
import { formatMoney } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CommunitySpaceManage',
  components: {
    AddCommunitySpace,
    EditCommunitySpace,
    EditCommunitySpaceOpenTime,
    DeleteCommunitySpace,
    AddCommunityVenue,
    EditCommunityVenue,
    DeleteCommunityVenue,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      venues: [],
      spaces: [],
      conditions: {
        spaceId: '',
        name: '',
        state: '',
        venueId: '',
        page: 1,
        row: 10
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.listVenues()
  },
  methods: {
    async listVenues() {
      try {
        const res = await listCommunityVenue({
          page: 1,
          row: 100
        })
        this.venues = Array.isArray(res.data) ? res.data : []
        if (this.venues.length > 0) {
          this.selectVenue(this.venues[0])
        }
      } catch (error) {
        console.error('获取场馆列表失败:', error)
      }
    },
    async fetchSpaces() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const res = await listCommunitySpace(this.conditions)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.spaces = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('communitySpaceManage.page.loadFailed')
        console.error('获取场地列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    selectVenue(venue) {
      this.conditions.venueId = venue.venueId
      this.page.current = 1
      this.fetchSpaces()
    },
    querySpaces() {
      this.conditions.page = 1
      this.page.current = 1
      this.fetchSpaces()
    },
    resetConditions() {
      this.conditions.spaceId = ''
      this.conditions.name = ''
      this.conditions.state = ''
      this.querySpaces()
    },
    handlePageChange(page) {
      this.conditions.page = page
      this.page.current = page
      this.fetchSpaces()
    },
    handleSizeChange(size) {
      this.conditions.row = size
      this.page.size = size
      this.page.current = 1
      this.fetchSpaces()
    },
    openAddSpaceModal() {
      if (!this.conditions.venueId) {
        this.$message.warning(this.$t('communitySpaceManage.selectVenue'))
        return
      }
      this.$refs.addSpaceModal.open(this.conditions.venueId)
    },
    openEditSpaceModal(space) {
      this.$refs.editSpaceModal.open(space)
    },
    openEditOpenTime(space) {
      this.$refs.editOpenTimeModal.open(space)
    },
    openDeleteSpaceModal(space) {
      this.$refs.deleteSpaceModal.open(space)
    },
    openAddVenueModal() {
      this.$refs.addVenueModal.open()
    },
    openEditVenueModal() {
      if (!this.conditions.venueId) {
        this.$message.warning(this.$t('communitySpaceManage.selectVenue'))
        return
      }
      const venue = this.venues.find(v => v.venueId === this.conditions.venueId)
      if (venue) {
        this.$refs.editVenueModal.open(venue)
      }
    },
    openDeleteVenueModal() {
      if (!this.conditions.venueId) {
        this.$message.warning(this.$t('communitySpaceManage.selectVenue'))
        return
      }
      const venue = this.venues.find(v => v.venueId === this.conditions.venueId)
      if (venue) {
        this.$refs.deleteVenueModal.open(venue)
      }
    },
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    },
    /**
     * 场地状态语义类型（1001 可预约 / 2002 不可预约）
     */
    resolveSpaceState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'communitySpace',
        text: row.stateName || ''
      }).type
    },
    /**
     * 场地状态文案：优先使用后端字典文案，缺失时回落到本地 i18n。
     */
    resolveSpaceStateLabel(row) {
      if (!row) return '-'
      if (row.stateName) return row.stateName
      const key = `communitySpaceManage.stateMap.${row.state}`
      const text = this.$t(key)
      return text === key ? '-' : text
    }
  }
}
</script>

<style lang="scss" scoped>
.community-space-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧场馆导航卡片 */
.ds-nav-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-xs);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__scroll {
    max-height: calc(100vh - 320px);
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: var(--ds-spacing-sm);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--ds-spacing-sm);
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-xs);
    border-radius: var(--ds-radius-sm);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-700);
    cursor: pointer;
    transition: background-color var(--ds-transition-fast),
      color var(--ds-transition-fast);

    &:hover {
      background-color: rgba(var(--ds-primary-rgb), 0.08);
      color: var(--ds-primary);
    }

    &.is-active {
      background-color: var(--ds-primary);
      color: #fff;
      font-weight: 600;
    }
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__info {
    color: var(--ds-gray-400);
    cursor: pointer;
    transition: color var(--ds-transition-fast);

    &:hover {
      color: var(--ds-primary);
    }
  }
}
</style>
